<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>{{ $t("usuarios.titulo") }}</h1>
      <div class="toolbar">
        <button type="button" @click.prevent="crearUsuario()" class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>

    <DxDataGrid v-bind="getDefaultGridProperties()" :data-source="gridSource" ref="grid" class="dx-card wide-card grid-operaciones">
      <DxColumnFixing :enabled="true" />
      <DxColumn data-field="Nombre" :caption="$t('usuarios.grid.nombre')"></DxColumn>
      <DxColumn data-field="Apellidos" :caption="$t('usuarios.grid.apellidos')"></DxColumn>
      <DxColumn data-field="Email" :caption="$t('usuarios.grid.email')"></DxColumn>
      <DxColumn data-field="Documento" :caption="$t('usuarios.grid.dni_nie')"></DxColumn>
      <DxColumn data-field="Telefono" :caption="$t('usuarios.grid.telefono')"></DxColumn>
      <DxColumn data-field="Empresa" :caption="$t('usuarios.grid.empresa')"></DxColumn>
      <DxColumn data-field="TipoEmpresaAgente" :caption="$t('usuarios.grid.tipo_empresa_agente')" cell-template="tipoEmpresaAgenteTemplate">
        <DxLookup v-bind="getLookup('empresa_agente', 'tipo')" />
      </DxColumn>
      <DxColumn data-field="CodigoSubtipo" :caption="$t('usuarios.grid.subtipoEmpresaColaboradora')" cell-template="subtipoEmpresaColaboradoraTemplate">
        <DxLookup v-bind="getLookup('empresa_colaboradora', 'subtipo')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="NombreComercial" :caption="$t('usuarios.grid.comercial')"></DxColumn>
      <DxColumn
        data-field="Rol"
        :caption="$t('usuarios.grid.rol')"
        cell-template="rolesTemplate"
        :filter-operations="['contains']"
        :calculate-filter-expression="calculateFilterExpression"
      >
      </DxColumn>
      <DxColumn data-field="Estado" :caption="$t('usuarios.grid.estado')" cell-template="estadoTemplate">
        <DxLookup column-auto-width="true" v-bind="getLookupEstado()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="Validado" :caption="$t('usuarios.grid.validado')" cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="FechaUltimoAcceso" :caption="$t('usuarios.grid.fecha_ultimo_acceso')" data-type="datetime" format="dd/MM/yyyy HH:mm:ss"></DxColumn>
      <DxColumn data-field="FechaBaja" :caption="$t('usuarios.grid.fecha_baja')" data-type="datetime" format="dd/MM/yyyy HH:mm:ss"></DxColumn>
      <DxColumn
        type="buttons"
        :caption="$t('usuarios.grid.acciones')"
        :fixed="true"
        fixed-position="right"
        width="140px"
        :buttons="[
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('usuarios.grid.modificar'),
            icon: 'fa-regular fa-edit text-success',
            onClick: (e: any) => verDetalle(e.row?.data.IdUsuario),
            disabled: (options: any) => !puedeEditar(options.row?.data),
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('usuarios.grid.borrar'),
            icon: 'fa-regular fa-trash text-success',
            onClick: (e: any) => borrar(e.row?.data.IdUsuario),
            disabled: (options: any) => options.row?.data.FechaBaja != null || !puedeEditar(options.row?.data) || options.row?.data.IdUsuario === currentUserId,
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('usuarios.grid.reactivar'),
            icon: 'fa-regular fa-user-unlock text-success',
            onClick: (e: any) => reactivar(e.row?.data.IdUsuario),
            disabled: (options: any) => options.row?.data.FechaBaja == null || !puedeEditar(options.row?.data),
          },
        ]"
      />

      <template #rolesTemplate="{ data }">
        <div class="roles">
          <div
            v-for="(role, index) in (data.value || '').split(',').map((role: string) => role.trim())"
            :key="role"
            class="d-inline-block rounded py-1 px-2"
            :class="`rol ${role}`"
          >
              {{ $t(`usuarios.rol.${role}`) }}
          </div>
        </div>
      </template>
      <template #estadoTemplate="{ data }">
        <div v-if="data.value" :class="[`d-inline-block rounded py-1 px-2`, `estado ${data.value}`]">
          
            {{ $t(`agentes.estado.${data.value}`) }}
          
        </div>
      </template>
      <template #booleanTemplate="{ data }">
        <BooleanTemplate :value="data.value" />
      </template>
      <template #tipoEmpresaAgenteTemplate="{ data }">
        <div v-if="data.value" :class="[`d-inline-block rounded py-1 px-2 `, `tipoEmpresaAgente ${data.value.replace(' ', '')}`]">
            {{ $t(`empresa_agente.tipo.${data.value}`) }}
        </div>
      </template>
      <template #subtipoEmpresaColaboradoraTemplate="{ data }">
        <div v-if="data.value" :class="[`d-inline-block rounded py-1 px-2`, `subtipoEmpresaColaboradora ${data.value.replace(' ', '')}`]">
            {{ $t(`empresa_colaboradora.subtipo.${data.value}`) }}
        </div>
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import { DxDataGrid, DxColumn, DxColumnFixing, DxLookup } from "devextreme-vue/data-grid";
import { getDefaultGridProperties, getLookup, getColumnLookupBoolean } from "@helpers/grid-helper";
import { useApi } from "@api/integration";
import type CustomStore from "devextreme/data/custom_store";
import { useIntl } from "@services/i18n";
import { useModalService } from "@services/modal-service";
import type { ColumnLookup } from "devextreme/ui/data_grid";
import UsuarioForm from "@/components/usuarios/UsuarioForm.vue";
import { messageBoxResult } from "@components/dialogs/message-box";
import { type DXLoadOptions } from "@/.shared/.api";
import BooleanTemplate from "@/.shared/components/BooleanTemplate.vue";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import { CreateUsuario } from "@/components/usuarios/models/CreateUsuario";
import { useAuthentication, ExtendedUserInfo } from "@/.shared/services/auth";

const intl = useIntl();
const api = useApi();
const auth = useAuthentication();
const grid = ref<DxDataGrid>();
const gridSource = ref<CustomStore>();
const modal = useModalService();

const currentUserId = computed(() => (auth.userInfo as ExtendedUserInfo)?.idUsuario);

const puedeEditar = (data: any) => {
  return !(data.Rol as string).includes("ADMINISTRADOR") && !data.GestionadoSistema;
};

onMounted(async () => {
  setDataSource();
});

function calculateFilterExpression(filterValue: string) {
  return [["Rol", "contains", filterValue.toUpperCase()]];
}

function getLookupEstado(): ColumnLookup {
  return getLookup("agentes", "estado");
}

async function crearUsuario() {
  let title = `${intl.i18n.t("usuario.creacion.titulo")}`;
  modal.showModal(UsuarioForm, { model: new CreateUsuario(), isEditMode: false }, { title: title, width: 800, height: 700 }).then((value) => {
    if (value == messageBoxResult.ok) grid.value?.instance?.refresh();
  });
}

const setDataSource = async () => {
  gridSource.value = api.apiStore({
    key: "IdUsuario",
    load: async (p) => {
      const q = p as DXLoadOptions;
      return await api.callStandardApi("getUsuariosForGrid", { showLoading: false }, q);
    },
  });
};

const verDetalle = async (idUsuario: string) => {
  let title = `${intl.i18n.t("usuarios.edicion.titulo")}`;
  modal
    .showModal(
      UsuarioForm,
      { model: await api.callStandardApi("getUsuarioById", { showLoading: true }, idUsuario), isEditMode: true },
      { title: title, width: 800, height: 700 },
    )
    .then((value) => {
      if (value == messageBoxResult.ok) grid.value?.instance?.refresh();
    });
};

const borrar = async (idUsuario: string) => {
  let respuesta = await modal.messageBox(
    intl.i18n.t("usuarios.grid.borrar_pregunta"),
    intl.i18n.t("usuarios.grid.titulo_confirma_borrar"),
    "okCancel",
    "warning",
  );
  if (respuesta == messageBoxResult.ok)
    await api
      .callStandardApi("deleteUsuario", { showLoading: true }, idUsuario)
      .then(() => {
        toastSuccess(intl.i18n.t("usuarios.borrado_exito"));
        grid.value?.instance?.refresh();
      })
      .catch(() => toastError(intl.i18n.t("usuarios.borrado_fallo")));
};

const reactivar = async (idUsuario: string) => {
  let respuesta = await modal.messageBox(
    intl.i18n.t("usuarios.grid.reactivar_pregunta"),
    intl.i18n.t("usuarios.grid.titulo_confirma_reactivar"),
    "okCancel",
    "warning",
  );
  if (respuesta == messageBoxResult.ok)
    await api
      .callStandardApi("reactivateUsuario", { showLoading: true }, idUsuario)
      .then(() => {
        toastSuccess(intl.i18n.t("usuarios.reactivado_exito"));
        grid.value?.instance?.refresh();
      })
      .catch(() => toastError(intl.i18n.t("usuarios.reactivado_fallo")));
};
</script>

<style lang="scss" scoped>



.roles span:nth-child(n + 2) {
  margin-top: 5px;
}

</style>
